import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { ApiCall } from "../helper/axios";
const MODULE_KEY = "publisher_reporting";
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const getDefaultDateRange = () => {
    const today = moment();
    const isFirstDayOfMonth = today.date() === 1;
    const start = isFirstDayOfMonth
        ? today.clone().subtract(1, "month").startOf("month")
        : today.clone().startOf("month");
    const end = today.clone().subtract(1, "day");
    // Default comparison: same days into previous month (MoM)
    const compareStart = start.clone().subtract(1, "month");
    const compareEnd = end.clone().subtract(1, "month");
    return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        compareStartDate: compareStart.format("YYYY-MM-DD"),
        compareEndDate: compareEnd.format("YYYY-MM-DD"),
    };
};
const getInitialState = () => {
    let saved = null;
    try {
        const raw = localStorage.getItem("publisher_reporting_filters");
        if (raw)
            saved = JSON.parse(raw);
    }
    catch { }
    const defaults = getDefaultDateRange();
    return {
        view: "publisher",
        rowsView: "publisher",
        rows: [],
        total: 0,
        pagination: null,
        startDate: saved?.startDate || defaults.startDate,
        endDate: saved?.endDate || defaults.endDate,
        compareStartDate: saved?.compareStartDate ?? defaults.compareStartDate,
        compareEndDate: saved?.compareEndDate ?? defaults.compareEndDate,
        funnel: saved?.funnel || "All",
        hideZeroClicks: saved?.hideZeroClicks ?? false,
        groupPublisherIds: saved?.groupPublisherIds || [],
        clientId: "",
        accountManagerId: "",
        outreachSpecialistId: "",
        category: "",
        publisherOptions: [],
        categoryOptions: [],
        summary: null,
        page: 1,
        limit: 100,
        search: "",
        sortBy: "revenue",
        sortOrder: "desc",
        columnFilters: {},
        loading: false,
        error: null,
        lastSyncedAt: null,
    };
};
const initialState = getInitialState();
export const fetchPublisherReporting = createAsyncThunk("publisherReporting/fetch", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState().publisherReporting;
        const token = getState().auth?.token || "";
        const payload = {
            type: state.view,
            startDate: state.startDate,
            endDate: state.endDate,
            funnel: state.funnel,
            hideZeroClicks: state.hideZeroClicks,
            groupPublisherIds: state.groupPublisherIds,
            clientId: state.clientId,
            accountManagerId: state.accountManagerId,
            outreachSpecialistId: state.outreachSpecialistId,
            category: state.category,
            search: state.search,
            page: state.page,
            limit: state.limit,
            sortBy: state.sortBy,
            sortOrder: state.sortOrder,
            columnFilters: state.columnFilters,
            compareStartDate: state.compareStartDate || "",
            compareEndDate: state.compareEndDate || "",
        };
        const response = await ApiCall("POST", "/api/publisher-reporting/list", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return { ...data.data, view: state.view };
        return rejectWithValue(data?.message || "Failed to fetch reporting data");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
// ==========================================================================
// SLICE
// ==========================================================================
export const fetchReportingFilterOptions = createAsyncThunk("publisherReporting/fetchFilterOptions", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("GET", "/api/publisher-tagging/filter-options", undefined, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to fetch filter options");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchReportingCategories = createAsyncThunk("publisherReporting/fetchCategories", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState().publisherReporting;
        const token = getState().auth?.token || "";
        // Options = categories of the placements in the current list (all filters except the category one)
        const response = await ApiCall("POST", "/api/publisher-reporting/categories", {
            type: state.view,
            startDate: state.startDate,
            endDate: state.endDate,
            funnel: state.funnel,
            groupPublisherIds: state.groupPublisherIds,
            clientId: state.clientId,
            accountManagerId: state.accountManagerId,
            outreachSpecialistId: state.outreachSpecialistId,
        }, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to fetch categories");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
const publisherReportingSlice = createSlice({
    name: "publisherReporting",
    initialState,
    reducers: {
        setFilters: (state, action) => {
            const nextState = { ...state, ...action.payload, page: 1 };
            try {
                const current = JSON.parse(localStorage.getItem("publisher_reporting_filters") || "{}");
                localStorage.setItem("publisher_reporting_filters", JSON.stringify({
                    ...current,
                    startDate: nextState.startDate,
                    endDate: nextState.endDate,
                    compareStartDate: nextState.compareStartDate,
                    compareEndDate: nextState.compareEndDate,
                    funnel: nextState.funnel,
                    hideZeroClicks: nextState.hideZeroClicks,
                    groupPublisherIds: nextState.groupPublisherIds,
                }));
            }
            catch { }
            return nextState;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPagination: (state, action) => {
            if (action.payload.page)
                state.page = action.payload.page;
            if (action.payload.limit)
                state.limit = action.payload.limit;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPublisherReporting.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchPublisherReporting.fulfilled, (state, action) => {
            // Response of a tab the user already left - ignore it (the newer request is still loading)
            if (action.payload.view !== state.view)
                return;
            state.loading = false;
            state.rowsView = action.payload.view;
            state.rows = action.payload.reporting || [];
            state.total = action.payload.total || 0;
            state.summary = action.payload.summary || null;
            state.lastSyncedAt = action.payload.lastSyncedAt || null;
            state.pagination = {
                total: action.payload.total,
                page: action.payload.page,
                limit: action.payload.limit,
            };
            if (action.payload.startDate)
                state.startDate = action.payload.startDate;
            if (action.payload.endDate)
                state.endDate = action.payload.endDate;
            if (action.payload.compareStartDate !== undefined)
                state.compareStartDate = action.payload.compareStartDate || "";
            if (action.payload.compareEndDate !== undefined)
                state.compareEndDate = action.payload.compareEndDate || "";
        })
            .addCase(fetchPublisherReporting.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch";
        })
            .addCase(fetchReportingFilterOptions.fulfilled, (state, action) => {
            state.publisherOptions = action.payload.group_publisher_names || [];
        })
            .addCase(fetchReportingCategories.fulfilled, (state, action) => {
            state.categoryOptions = action.payload.categories || [];
            // The chosen category has no placements in the new list (tab / dates / filters changed): back to All
            if (state.category && !state.categoryOptions.some((c) => c.toLowerCase() === state.category.toLowerCase())) {
                state.category = "";
                state.page = 1;
            }
        });
    },
});
export const { setFilters, setPage, setPagination } = publisherReportingSlice.actions;
export default publisherReportingSlice.reducer;
